import React, { useEffect, useState } from "react";

import FacilityCreate from "./FacilityCreate";

import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import { getAdminUsers, getUsers } from "../../../redux/reducers/adminUsers";
import { getAuthToken } from "../../../redux/reducers/auth";

import { FieldState } from "../../../utils/ValidationFunctions";
import { RequestState } from "../../../utils/RequestState";
import { useUpdateFacilityAdmin } from "../../../utils/useFacility";
import FacilityForm from "./FacilityForm";
import { PageVisibility } from "../../../utils/PageVisibility";
import {
  convertFacilityTraits,
  convertFacilityTypes,
} from "../../../utils/FacilityState";

const FacilityCreateContainer: React.FC = () => {
  const queryKey = {};
  const { mutateAsync, mutateLoading } = useUpdateFacilityAdmin([
    "facilityAdmin",
    queryKey,
  ]);

  const dispatch = useAppDispatch();
  const users = useAppSelector(getAdminUsers);
  const authToken = useAppSelector(getAuthToken);

  const [fieldState, setFieldState] = useState<FieldState>();
  const [imgItems, setImgItems] = useState<File[]>([]);
  const [traits, setTraits] = useState<Array<string>>([]);
  const [types, setTypes] = useState<Array<string>>([]);
  const [ownerName, setOwnerName] = useState<string>("");
  const [selectStatus, setSelectStatus] = useState<string>(
    PageVisibility.NotPubliclyVisible
  );
  const [showStatusMenu, setShowStatusMenu] = useState<boolean>(false);
  const [requestState, setRequestState] = useState<RequestState>(
    RequestState.Waiting
  );
  const [error, setError] = useState<boolean>(false);

  const handleStatusMenu = () => {
    setShowStatusMenu(!showStatusMenu);
  };

  const handleUpdate = async () => {
    if (!authToken) return;
    if (
      !fieldState ||
      !fieldState["sonic_name"] ||
      fieldState["sonic_name"][0] === ""
    ) {
      setError(true);
      return;
    }

    setError(false);
    setRequestState(RequestState.Doing);

    const update_args: { [key: string]: string | File } = {};

    for (const key in fieldState) {
      if (fieldState[key][1] == false) {
        return;
      }

      const val = fieldState[key][0];

      if (typeof val === "string") {
        update_args[key] = val;
      }
    }

    if (imgItems) {
      imgItems.forEach((v, i) => {
        if (v instanceof File) {
          update_args[`sonic_imgs${i}`] = v;
        } else {
          update_args[`sonic_imgs${i}`] = "";
        }
      });
    }

    const user = users.find((u) => u.meta.name === ownerName);
    update_args["sonic_owner"] = user ? user.id : "";

    if (traits.length > 0) {
      update_args["sonic_traits"] = JSON.stringify(
        convertFacilityTraits(traits)
      );
    }

    if (types.length > 0) {
      update_args["sonic_facility_types"] = JSON.stringify(
        convertFacilityTypes(types)
      );
    }

    if (selectStatus) {
      update_args["sonic_status"] = selectStatus;
    }

    await mutateAsync({ authToken, update: update_args });

    setRequestState(RequestState.Success);
    setSelectStatus(PageVisibility.NotPubliclyVisible);
    setImgItems([]);
    setOwnerName("");
    setFieldState({});
    setTraits([]);
    setTypes([]);
  };

  const handleInputChange = (
    value: File | string | undefined,
    name: string
  ) => {
    let f: FieldState = {};
    if (fieldState !== undefined) {
      f = fieldState;
    }

    if (value === undefined) {
      if (name !== "sonic_imgs") f[name] = ["", false];
    } else {
      if (value instanceof File) {
        if (!imgItems) {
          setImgItems([value]);
          return;
        } else {
          const newList = [...imgItems, value];
          setImgItems(newList);
          return;
        }
      } else {
        if (name !== "sonic_imgs") f[name] = [value, true];
      }
      setRequestState(RequestState.Pending);
    }
    setFieldState({ ...f });
  };

  const deleteImgItem = (i: number) => {
    const newList = imgItems.filter((_, index) => index !== i);
    setImgItems(newList);
  };

  const changeTraits = (value: string) => {
    if (traits.includes(value)) {
      setTraits(traits.filter((trait) => trait !== value));
    } else {
      setTraits([...traits, value]);
    }
  };

  const changeTypes = (value: string) => {
    if (types.includes(value)) {
      setTypes(types.filter((type) => type !== value));
    } else {
      setTypes([...types, value]);
    }
  };

  useEffect(() => {
    if (authToken)
      dispatch(
        getUsers({ auth_token: authToken, isSelect: true, params: { per: -1 } })
      );
  }, []);

  return (
    <FacilityCreate
      isLoading={mutateLoading}
      requestState={requestState}
      error={error}
      selectStatus={selectStatus}
      setSelectStatus={setSelectStatus}
      showStatusMenu={showStatusMenu}
      handleStatusMenu={handleStatusMenu}
      handleUpdate={handleUpdate}
    >
      <FacilityForm
        users={users}
        imgItems={imgItems}
        traits={traits}
        types={types}
        ownerName={ownerName}
        setOwnerName={setOwnerName}
        handleInputChange={handleInputChange}
        deleteImgItem={deleteImgItem}
        handleTraits={changeTraits}
        handleTypes={changeTypes}
      />
    </FacilityCreate>
  );
};

export default FacilityCreateContainer;
